import { IStorageService, storageProvider } from '@root/services/StorageService';
import { action, observable } from 'mobx';
import { ExampleModel, OperationModel } from 'redoc';

export interface EndpointTesterResponse {
    status: number;
    headers: Record<string, string>;
    body: string;
    durationMs: number;
}

interface StagedRequest {
    token: string;
    body: string;
    path: string;
    method: string;
    queryParams: Array<{ name: string; value: string }>;
}
export interface RequestBodyExample {
    name: string;
    value: string;
    select: () => void;
}

export class RequestEditorModel {
    public get hasSecurity() {
        return this.operation.security.length > 0;
    }

    public readonly stagedRequest: StagedRequest;

    public examples: Array<RequestBodyExample> = [];

    public reqHistory: IStorageService;

    public constructor(
        public readonly operation: OperationModel,
        public readonly baseUrl?: string,
    ) {
        const { path, httpVerb: method, requestBody } = operation;

        this.reqHistory = storageProvider.get(`req-history`, 'cross-tab');

        const exampleMap = requestBody?.content?.active?.examples || {};

        this.examples = [...Object.entries(exampleMap)].map(([key, example]) =>
            RequestEditorModel.createExampleOption(example, (value) => (this.stagedRequest.body = value)),
        );

        const body = this.examples[0]?.value || '{}';

        this.stagedRequest = observable({ token: '', body, path, method, queryParams: [] });
    }

    public sendRequest = () => this.send();

    private async send(): Promise<EndpointTesterResponse> {
        try {
            const queryParams = this.stagedRequest.queryParams.map((q) => `${encodeURIComponent(q.name)}=${encodeURIComponent(q.value)}`).join('&');
            const url = (this.baseUrl || '') + this.operation.path + (queryParams ? `?${queryParams}` : '');

            const startTime = performance.now();
            const response = await fetch(url, {
                method: this.stagedRequest.method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this.stagedRequest.token}`,
                },
                body: this.stagedRequest.body,
                mode: 'cors',
            });
            const durationMs = performance.now() - startTime;
            this.saveHistory(this.stagedRequest.body, this.stagedRequest.queryParams);

            return {
                status: response.status,
                headers: response.headers.entries().reduce(
                    (acc, [key, value]) => {
                        acc[key] = value;
                        return acc;
                    },
                    {} as Record<string, string>,
                ),
                body: this.formatJsonResponse(await response.text()),
                durationMs,
            };
        } catch (error) {
            console.error('Error sending request:', error);
            return {
                status: -1,
                headers: {},
                body: 'Error sending request',
                durationMs: -1,
            };
        }
    }

    private async saveHistory(body: string, params: Array<{ name: string; value: string }>) {
        try {
            if (!this.operation.path.toLowerCase().startsWith('/auth/token')) {
                this.reqHistory.save(null, {
                    opId: this.operation.id,
                    body,
                    params,
                    date: new Date().toISOString(),
                });
            }
        } catch (error) {
            console.error('Error saving request history:', error);
        }
    }

    private formatJsonResponse(value: string): string {
        try {
            const json = JSON.parse(value);
            return JSON.stringify(json, null, 2);
        } catch {
            return value; // If parsing fails, return the original value
        }
    }

    private static createExampleOption(example: ExampleModel, select: (value: string) => void) {
        const { description, value, summary } = example;
        const formattedValue = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
        return {
            name: summary || description || 'Example',
            value: formattedValue,
            select: () => select(formattedValue),
        };
    }
}
